/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `SSOCallback` for the Caintra Connect frontend.
 * @module pages/SSOCallback
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuthSession } from "../context/AuthSessionContext";
import { getUserRoleData, handleSSOCallback } from "../logic/auth/index.js";

const SSOCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { establishSession } = useAuthSession();

  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code) {
        // Send the code to your backend Lambda (the callback function)
        try {
          const loginData = await handleSSOCallback(code);
          const tokens = loginData.tokens;
          const userId = loginData.userId;

          localStorage.setItem("userId", userId);
          localStorage.setItem("token", tokens.access_token);
          localStorage.setItem("refreshToken", tokens.refresh_token);

          const userRoleData = await getUserRoleData({
            token: tokens.access_token,
            userId: userId,
          });

          await establishSession({
            permissions: userRoleData?.permisos ?? null,
          });

          window.dataLayer.push({
            event: "login",
            user_id: userId,
          });

          navigate("/home");
        } catch (error) {
          logger.error("Error during SSO callback handling:", {
            err: sanitizeError(error),
          });
          // navigate("/");
          return;
        }
      } else {
        logger.error("No code found in URL");
        navigate("/");
      }
    };

    handleCallback();
  }, [establishSession, navigate]);

  return <div>{t("common.signingIn")}</div>;
};

export default SSOCallback;
